import { BlockchainIcon } from "@/app/blockchains/blockchain-icon/blockchain-icon"
import { useBlockchain } from "@/app/blockchains/use-blockchain"
import { MintingNow } from "@/app/components/minting-now"
import { VerifiedIcon } from "@/app/icons/verified"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import type { Drop } from "@anaconda/core/drop/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { useLingui } from "@lingui/react/macro"
import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import Image from "next/image"
import { CollectionImage } from "../../view/collection-image"
import type { LeaderboardItem } from "../store.reatom"

interface Props {
  collectionId: CollectionId
  image?: string
  blockchain: Blockchain
  showBlockchain: boolean
  name: string
  verified: boolean
  className?: string
  drop?: Drop
  statistics?: LeaderboardItem["statistics"]
}

export function CollectionPreview(props: Props) {
  const { t } = useLingui()
  const { icon } = useBlockchain(props.blockchain)

  return (
    <div className={cn("flex w-full items-center gap-x-3", props.className)}>
      <div className="relative flex-none">
        <CollectionImage imageSrc={props.image} collectionName={props.name} />
        {props.showBlockchain && (
          <Image
            unoptimized
            className="absolute right-0 bottom-0 z-20 translate-x-1/2 translate-y-1/2 transform"
            src={icon}
            width={12}
            height={12}
            alt={t`Blockchain icon for ${props.blockchain}`}
          />
        )}
      </div>
      <div className="flex w-[calc(100%-2.75rem)] items-center gap-x-1">
        <div className="truncate">{props.name}</div>
        {props.verified && <VerifiedIcon className="flex-none" />}

        {props.drop && (
          <MintingNow
            collectionId={props.collectionId}
            drop={props.drop}
            items={props.statistics?.items ?? undefined}
            withLink={false}
          />
        )}
      </div>
    </div>
  )
}

export function CollectionPreviewSkeleton() {
  return (
    <div className="flex items-center gap-x-3">
      <Skeleton className="h-8 w-8 rounded-md border" />
      <Skeleton className="h-2.5 w-[122px]" />
    </div>
  )
}
